import React from 'react';
import { Box, Typography, Stack } from '@mui/material';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
const stepTitles = [
  'Upload Rides',
  'Create Fleet',
  'Calculate Time Windows',
  'Create Problem Config',
  'Run Simulation'
];

const stageIcons = [
  ICON_FALLBACKS.UploadOutlined,
  ICON_FALLBACKS.GroupsOutlined,
  ICON_FALLBACKS.AccessTimeOutlined,
  ICON_FALLBACKS.SettingsOutlined,
  ICON_FALLBACKS.PlayArrowOutlined,
];

interface SimulationStepTrackerPanelProps {
  stepStatuses: ('notStarted' | 'inProgress' | 'success' | 'failed')[];
  title?: string;
}

const SimulationStepTrackerPanel: React.FC<SimulationStepTrackerPanelProps> = ({ 
  stepStatuses, 
  title = "Simulation Progress" 
}) => {
  const getStatusIcon = (status: 'notStarted' | 'inProgress' | 'success' | 'failed') => {
    switch (status) {
      case 'notStarted':
        return <Icon size={20} name="circle" style={{ color: '#bdbdbd' }} />;
      case 'inProgress':
        return <Icon size={20} d={ICON_FALLBACKS.RadioButtonChecked} style={{ color: '#ef7335' }} />;
      case 'success':
        return <Icon size={20} name="circle-check" style={{ color: '#2e7d32' }} />;
      case 'failed':
        return <Icon size={20} name="circle-exclamation" style={{ color: '#d32f2f' }} />;
      default:
        return <Icon size={20} name="circle" style={{ color: '#bdbdbd' }} />;
    }
  };

  const getStatusText = (status: 'notStarted' | 'inProgress' | 'success' | 'failed') => {
    switch (status) {
      case 'notStarted':
        return 'Not Started';
      case 'inProgress':
        return 'In Progress';
      case 'success':
        return 'Completed';
      case 'failed':
        return 'Failed';
      default:
        return 'Not Started';
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      {title ? (
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
      ) : null}
      <Box
        sx={{ display: 'flex', alignItems: 'center', overflowX: 'auto', pb: 0.5, gap: 0.75 }}
      >
        {stepTitles.map((stepTitle, idx) => {
          const status = stepStatuses[idx] || 'notStarted';
          const stageIconPath = stageIcons[idx] || ICON_FALLBACKS.RadioButtonChecked;
          return (
            <React.Fragment key={stepTitle}>
              <Box
                sx={{
                  flex: '1 1 0',
                  minWidth: 0,
                  minHeight: 116,
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: 34,
                    fontWeight: status === 'inProgress' ? 700 : 600,
                    color: status === 'inProgress' ? 'primary.main' : 'text.primary',
                    textAlign: 'center',
                    lineHeight: 1.35,
                  }}
                >
                  {stepTitle}
                </Typography>
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                      position: 'relative',
                      bgcolor: 'rgba(255,255,255,0.56)',
                    }}
                  >
                    <Icon size={24} d={stageIconPath} />
                    <Box sx={{ position: 'absolute', right: -6, top: -6, bgcolor: 'background.paper', borderRadius: '50%' }}>
                      {getStatusIcon(status)}
                    </Box>
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.75,
                    display: 'block',
                    textAlign: 'center',
                    color:
                      status === 'success'
                        ? 'success.main'
                        : status === 'failed'
                        ? 'error.main'
                        : status === 'inProgress'
                        ? 'primary.main'
                        : 'text.secondary',
                    fontWeight: status === 'inProgress' ? 600 : 500,
                  }}
                >
                  {getStatusText(status)}
                </Typography>
              </Box>
              {idx < stepTitles.length - 1 && (
                <Box
                  aria-hidden
                  sx={{
                    width: 14,
                    flexShrink: 0,
                    height: 2,
                    borderRadius: 999,
                    bgcolor: status === 'success' ? 'success.main' : 'divider',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default SimulationStepTrackerPanel; 